import { useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { useMount, useUnmount } from "react-use"

import { OwlDowntimeImage } from "@/3514/assets"
import {
    Button,
    EButtonSize,
    ETypographyAlignment,
    ETypographySize,
    ETypographyWeight,
    Modal,
    Typography
} from "@/3514/components"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

const classes = {
    modalBody: buildCssClassName(
        "px-[45px]",
        "py-[35px]",
        "bg-white",
        "max-w-[520px]",
        "w-[90%]",
        "md:w-[100%]",
        "md:w-full",
        "min-h-min",
        "md:h-fit",
        "rounded-[10px]",
        "bg-white"
    ),
    modalBodyContainer: "flex flex-col items-center gap-y-[15px]",
    modalBodyTextReset: "mb-0",
    modalBodyTextSelection: "select-none",
    modalBodyTextContainer: "flex flex-col gap-y-[5px] mb-[10px]"
}

/**
 * ReloadApp modal.
 * Design: {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=19295-38298&t=KQJfIijv8qIbCqXL-4}
 *
 * @return ReactElement
 */
const Container: FC = (): ReactElement => {
    const { hideModal }: TModalDispatcher = useModalDispatcher()

    const { t }: IUseTranslation = useTranslation()

    const handleAction: ReturnType<typeof withPressHandler> = withPressHandler((): void => {
        hideModal(EModalId.ReloadApp)
        window.location.reload()
    })

    const rootElement: RefObject<HTMLElement> = useRef<HTMLElement>(document.getElementById("main-app"))

    useMount((): void => {
        if (rootElement.current) rootElement.current.inert = true
    })

    useUnmount((): void => {
        if (rootElement) rootElement.current.inert = false
    })

    return (
        <Modal modalId={EModalId.ReloadApp} modalBodyClassName={classes.modalBody} withPortal withDisabledBodyScroll>
            <div className={classes.modalBodyContainer}>
                <OwlDowntimeImage />

                <div className={classes.modalBodyTextContainer}>
                    <Typography
                        size={ETypographySize.Small}
                        text={t("modals.reloadApp.title")}
                        alignment={ETypographyAlignment.Center}
                        weight={ETypographyWeight.Bold}
                        className={buildCssClassName(classes.modalBodyTextReset, classes.modalBodyTextSelection)}
                    />
                    <Typography
                        size={ETypographySize.Small}
                        text={t("modals.reloadApp.description")}
                        alignment={ETypographyAlignment.Center}
                        className={buildCssClassName(classes.modalBodyTextReset, classes.modalBodyTextSelection)}
                    />
                </div>

                <Button
                    onClick={handleAction}
                    onKeyDown={handleAction}
                    size={EButtonSize.Small}
                    className={classes.modalBodyTextSelection}
                    minWidth="140px"
                >
                    {t("modals.reloadApp.button")}
                </Button>
            </div>
        </Modal>
    )
}

Container.displayName = "ReloadAppModalContainer"

export { Container as ReloadAppModalContainer }
